// ComparisonWidget.js - Period-vs-period comparison
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';

const getBaseURL = () => {
  const hostname = window.location.hostname;
  if (hostname === 'dev.barriobucks.com') {
    return 'https://dev.barriobucks.com';
  }
  return 'https://barriobucks.com';
};

const api = axios.create({
  baseURL: `${getBaseURL()}/api`,
  withCredentials: true,
});

// Format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ComparisonWidget = ({ period, source }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getComparisonDates = useCallback(() => {
    const now = new Date();
    let period1Start, period1End, period2Start, period2End;
    let periodLabel = '';

    switch (period) {
      case 'today':
        period1End = formatLocalDate(now);
        period1Start = period1End;
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        period2End = formatLocalDate(yesterday);
        period2Start = period2End;
        periodLabel = 'vs Yesterday';
        break;

      case 'yesterday':
        const yd = new Date(now);
        yd.setDate(yd.getDate() - 1);
        period1End = formatLocalDate(yd);
        period1Start = period1End;
        const dayBefore = new Date(yd);
        dayBefore.setDate(dayBefore.getDate() - 1);
        period2End = formatLocalDate(dayBefore);
        period2Start = period2End;
        periodLabel = 'vs Day Before';
        break;

      case 'week':
        period1End = formatLocalDate(now);
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        period1Start = formatLocalDate(weekAgo);

        const twoWeeksAgo = new Date(weekAgo);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 1);
        period2End = formatLocalDate(twoWeeksAgo);
        const threeWeeksAgo = new Date(twoWeeksAgo);
        threeWeeksAgo.setDate(threeWeeksAgo.getDate() - 6);
        period2Start = formatLocalDate(threeWeeksAgo);
        periodLabel = 'vs Previous Week';
        break;

      case 'month':
      default:
        period1End = formatLocalDate(now);
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);
        period1Start = formatLocalDate(monthAgo);

        const twoMonthsAgo = new Date(monthAgo);
        twoMonthsAgo.setDate(twoMonthsAgo.getDate() - 1);
        period2End = formatLocalDate(twoMonthsAgo);
        const threeMonthsAgo = new Date(twoMonthsAgo);
        threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 29);
        period2Start = formatLocalDate(threeMonthsAgo);
        periodLabel = 'vs Previous Period';
        break;

      case 'quarter':
        period1End = formatLocalDate(now);
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        period1Start = formatLocalDate(quarterAgo);

        const twoQuartersAgo = new Date(quarterAgo);
        twoQuartersAgo.setDate(twoQuartersAgo.getDate() - 1);
        period2End = formatLocalDate(twoQuartersAgo);
        const threeQuartersAgo = new Date(twoQuartersAgo);
        threeQuartersAgo.setMonth(threeQuartersAgo.getMonth() - 3);
        threeQuartersAgo.setDate(threeQuartersAgo.getDate() + 1);
        period2Start = formatLocalDate(threeQuartersAgo);
        periodLabel = 'vs Previous Quarter';
        break;

      case 'year':
        period1End = formatLocalDate(now);
        const yearAgo = new Date(now);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        period1Start = formatLocalDate(yearAgo);

        const twoYearsAgo = new Date(yearAgo);
        twoYearsAgo.setDate(twoYearsAgo.getDate() - 1);
        period2End = formatLocalDate(twoYearsAgo);
        const threeYearsAgo = new Date(twoYearsAgo);
        threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 1);
        threeYearsAgo.setDate(threeYearsAgo.getDate() + 1);
        period2Start = formatLocalDate(threeYearsAgo);
        periodLabel = 'vs Previous Year';
        break;
    }

    return { period1Start, period1End, period2Start, period2End, periodLabel };
  }, [period]);

  const fetchComparison = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { period1Start, period1End, period2Start, period2End, periodLabel } = getComparisonDates();

      const params = {
        period1Start,
        period1End,
        period2Start,
        period2End,
        source: source !== 'all' ? source : undefined
      };

      const response = await api.get('/stats/compare', { params });

      setComparison({
        ...response.data,
        periodLabel
      });
    } catch (err) {
      console.error('Error fetching comparison:', err);
      setError('Failed to load comparison');
    } finally {
      setLoading(false);
    }
  }, [getComparisonDates, source]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  const renderMetric = (label, current, change) => {
    const changeNum = parseFloat(change) || 0;

    return (
      <div className="flex items-center justify-between py-3 border-b border-gray-700 last:border-b-0">
        <div>
          <div className="text-gray-400 text-sm">{label}</div>
          <div className="text-xl font-bold text-white">{current}</div>
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded ${
          changeNum > 0 ? 'bg-green-900/50 text-green-400' :
          changeNum < 0 ? 'bg-red-900/50 text-red-400' :
          'bg-gray-700 text-gray-400'
        }`}>
          {changeNum > 0 ? <FiTrendingUp className="w-4 h-4" /> :
           changeNum < 0 ? <FiTrendingDown className="w-4 h-4" /> :
           <FiMinus className="w-4 h-4" />}
          <span className="font-medium">{changeNum > 0 ? '+' : ''}{change}%</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-12 bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  if (!comparison) return null;

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(val) || 0);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Period Comparison</h3>
        <span className="text-sm text-gray-400">{comparison.periodLabel}</span>
      </div>

      <div>
        {renderMetric('Orders', comparison.period1?.order_count || 0, comparison.changes?.order_count)}
        {renderMetric('Revenue', formatCurrency(comparison.period1?.revenue), comparison.changes?.revenue)}
        {renderMetric('Avg Order', formatCurrency(comparison.period1?.avg_order), comparison.changes?.avg_order)}
        {renderMetric('Unique Customers', comparison.period1?.unique_customers || 0, comparison.changes?.unique_customers)}
      </div>
    </div>
  );
};

export default ComparisonWidget;
